/**
 * Shared PDF styles using @react-pdf/renderer
 * Professional styling for all PDF exports in the app
 */

import { StyleSheet } from '@react-pdf/renderer';

// Register fonts for professional look (using system fonts as fallback)
// Font.register({
//   family: 'Inter',
//   fonts: [
//     { src: '/fonts/Inter-Regular.ttf', fontWeight: 'normal' },
//     { src: '/fonts/Inter-Bold.ttf', fontWeight: 'bold' },
//   ],
// });

// Color palette matching app theme
export const colors = {
  primary: '#0064c8',
  primaryDark: '#004a99',
  secondary: '#00c8a0',
  accent: '#00f0ff',
  
  // Status colors
  success: '#22c55e',
  warning: '#f59e0b',
  error: '#ef4444',
  info: '#3b82f6',
  
  // Connection type colors
  call: '#3b82f6',
  sms: '#22c55e',
  browsing: '#8b5cf6',
  app: '#ec4899',
  dwell: '#f59e0b',
  
  // Priority colors
  critical: '#dc2626',
  high: '#f97316',
  normal: '#3b82f6',
  low: '#6b7280',
  
  // Neutrals
  white: '#ffffff',
  black: '#000000',
  gray100: '#f3f4f6',
  gray200: '#e5e7eb',
  gray300: '#d1d5db',
  gray400: '#9ca3af',
  gray500: '#6b7280',
  gray600: '#4b5563',
  gray700: '#374151',
  gray800: '#1f2937',
  gray900: '#111827',
  
  // Classification
  cui: '#7c3aed', // Purple for CUI banner
};

// Base styles used across all PDFs
export const baseStyles = StyleSheet.create({
  // Page layouts
  page: {
    padding: 30,
    paddingTop: 40,
    paddingBottom: 50,
    backgroundColor: colors.white,
    fontFamily: 'Helvetica',
    fontSize: 10,
    color: colors.gray800,
  },
  
  pageWithBanner: {
    padding: 30,
    paddingTop: 50,
    paddingBottom: 60,
    backgroundColor: colors.white,
    fontFamily: 'Helvetica',
    fontSize: 10,
    color: colors.gray800,
  },
  
  // Classification banner
  classificationBanner: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 20,
    backgroundColor: colors.cui,
    justifyContent: 'center',
    alignItems: 'center',
  },
  
  classificationBannerBottom: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 20,
    backgroundColor: colors.cui,
    justifyContent: 'center',
    alignItems: 'center',
  },
  
  classificationText: {
    color: colors.white,
    fontSize: 8,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  
  // Headers
  headerBanner: {
    backgroundColor: colors.primary,
    padding: 15,
    marginBottom: 20,
    marginHorizontal: -30,
    marginTop: -10,
  },
  
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: colors.white,
    marginBottom: 4,
  },
  
  headerSubtitle: {
    fontSize: 12,
    color: colors.white,
    opacity: 0.9,
  },
  
  headerMeta: {
    fontSize: 9,
    color: colors.white,
    opacity: 0.8,
    marginTop: 4,
  },
  
  // Sections
  section: {
    marginBottom: 15,
  },
  
  sectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: colors.gray200,
    paddingBottom: 4,
  },
  
  sectionSubtitle: {
    fontSize: 11,
    fontWeight: 'bold',
    color: colors.gray700,
    marginBottom: 6,
    marginTop: 8,
  },
  
  // Text styles
  text: {
    fontSize: 10,
    color: colors.gray700,
    lineHeight: 1.4,
  },
  
  textSmall: {
    fontSize: 8,
    color: colors.gray500,
  },
  
  textBold: {
    fontSize: 10,
    fontWeight: 'bold',
    color: colors.gray800,
  },
  
  textMuted: {
    fontSize: 9,
    color: colors.gray400,
  },
  
  // Lists
  listItem: {
    flexDirection: 'row',
    marginBottom: 3,
    paddingLeft: 5,
  },
  
  listBullet: {
    width: 12,
    fontSize: 10,
    color: colors.gray500,
  },
  
  listContent: {
    flex: 1,
    fontSize: 9,
    color: colors.gray700,
  },
  
  // Tables
  table: {
    marginTop: 5,
    marginBottom: 10,
  },
  
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: colors.gray100,
    borderBottomWidth: 1,
    borderBottomColor: colors.gray300,
    paddingVertical: 6,
    paddingHorizontal: 8,
  },
  
  tableHeaderCell: {
    fontSize: 9,
    fontWeight: 'bold',
    color: colors.gray700,
  },
  
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: colors.gray100,
    paddingVertical: 5,
    paddingHorizontal: 8,
  },
  
  tableCell: {
    fontSize: 9,
    color: colors.gray600,
  },
  
  // Cards/Boxes
  card: {
    backgroundColor: colors.gray100,
    borderRadius: 4,
    padding: 10,
    marginBottom: 10,
  },
  
  cardTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    color: colors.gray800,
    marginBottom: 6,
  },
  
  // Stats
  statsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 10,
  },
  
  statBox: {
    width: '25%',
    padding: 8,
  },
  
  statValue: {
    fontSize: 16,
    fontWeight: 'bold',
    color: colors.primary,
  },
  
  statLabel: {
    fontSize: 8,
    color: colors.gray500,
    marginTop: 2,
  },
  
  // Footer
  footer: {
    position: 'absolute',
    bottom: 25,
    left: 30,
    right: 30,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  
  footerText: {
    fontSize: 8,
    color: colors.gray400,
  },
  
  pageNumber: {
    fontSize: 8,
    color: colors.gray400,
  },
  
  // Badges/Tags
  badge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 3,
    marginRight: 5,
  },
  
  badgeText: {
    fontSize: 7,
    fontWeight: 'bold',
    color: colors.white,
  },
  
  // Two column layout
  twoColumn: {
    flexDirection: 'row',
    gap: 15,
  },
  
  column: {
    flex: 1,
  },
  
  // Divider
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: colors.gray200,
    marginVertical: 10,
  },
  
  // Connection type specific
  connectionCall: { color: colors.call },
  connectionSms: { color: colors.sms },
  connectionBrowsing: { color: colors.browsing },
  connectionApp: { color: colors.app },
  connectionDwell: { color: colors.dwell },
});

// Helper to get connection color
export const getConnectionColor = (type: string): string => {
  const colorMap: Record<string, string> = {
    call: colors.call,
    sms: colors.sms,
    browsing: colors.browsing,
    app: colors.app,
    dwell: colors.dwell,
  };
  return colorMap[type] || colors.gray500;
};

// Helper to get priority color
export const getPriorityColor = (priority: string): string => {
  const colorMap: Record<string, string> = {
    critical: colors.critical,
    high: colors.high,
    normal: colors.normal,
    low: colors.low,
  };
  return colorMap[priority] || colors.normal;
};

